import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'
import Tooltip from 'react-bootstrap/Tooltip'

import { InfoIcon } from '../InfoIcon'

import {
  formatCurrency,
  formatInteger,
  formatPaceDifference,
  formatSellThrough,
  getPaceStatusMeta,
  getSellThroughStatusMeta,
} from './pricingLevelPerformanceUtils'

import './pricingLevelPerformanceWrapper.scss'

function InfoTip({ children, label, placement = 'top' }) {
  return (
    <OverlayTrigger
      placement={placement}
      overlay={
        <Tooltip
          id="pricing-level-revenue-tip"
          className="pricing-level-revenue-tooltip"
        >
          {children}
        </Tooltip>
      }
    >
      <Button variant="link" aria-label={label}>
        <InfoIcon variant="dark" />
      </Button>
    </OverlayTrigger>
  )
}

function StatusCell({ status, children }) {
  const meta = getSellThroughStatusMeta(status)
  return (
    <span className={`status-cell status-cell--${status}`}>
      <span aria-hidden>{meta.emoji}</span>
      <span>{children}</span>
    </span>
  )
}

function PaceCell({ status, value }) {
  const meta = getPaceStatusMeta(status)
  return (
    <span className={`status-cell status-cell--${status}`}>
      <span aria-hidden>{meta.emoji}</span>
      <span>{formatPaceDifference(value)}</span>
    </span>
  )
}

export default function PricingLevelPerformanceMatrix({
  rows,
  cardClassName = 'card--xs card-with-border pricing-level-performance-matrix-card',
  titleAs: Title = 'h2',
}) {
  return (
    <Card
      body
      className={cardClassName}
      data-testid="pricing-level-performance-matrix"
    >
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-center flex-wrap gap-2 mb-3"
      >
        <Title className="h6 mb-0">Performance matrix</Title>
        <span className="text-muted small">
          {rows.length} pricing level{rows.length === 1 ? '' : 's'}
        </span>
      </Stack>

      <Table responsive className="pricing-level-performance-table">
        <thead>
          <tr>
            <th>Pricing Level</th>
            <th>Sold</th>
            <th>Remaining</th>
            <th>Sell Through</th>
            <th>Pace</th>
            <th>
              <span className="revenue-header">
                Revenue
                <InfoTip label="About revenue">
                  Gross charged for website and box office tickets on this
                  pricing level, including service and processing fees.
                  Complimentary tickets are excluded. Taken from the latest
                  daily snapshot.
                </InfoTip>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.pricingLevelId}>
              <td className="pricing-level-name">{row.pricingLevelName}</td>
              <td>
                <span className="metric-value">{formatInteger(row.sold)}</span>
              </td>
              <td>
                <span className="metric-value">
                  {formatInteger(row.remaining)}
                </span>
              </td>
              <td>
                <StatusCell status={row.sellThroughStatus}>
                  {formatSellThrough(row.sellThroughPercentage)}
                </StatusCell>
              </td>
              <td>
                <PaceCell
                  status={row.paceStatus}
                  value={row.paceDifferencePercentage}
                />
              </td>
              <td>
                <span className="metric-value">
                  {formatCurrency(row.grossRevenue)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="pricing-level-performance-legend">
        <span>🟢 Strong · sell-through 85%+ or pace +10%+</span>
        <span>🟡 Watch · sell-through 61–84% or pace -5% to +10%</span>
        <span>🔴 At risk · sell-through 60% or below or pace below -5%</span>
      </div>
    </Card>
  )
}
